import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import { getMessages, getMessage, AdminMessage, AdminMessageDetail } from "@services/adminService";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import usePolling from "@src/hooks/usePolling";
import { useDebouncedValue } from "@src/hooks/useDebouncedValue";

// ── Styled Components ──────────────────────────────────────

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterInput = styled(Input)`
  height: 36px;
  width: 200px;
  min-width: 140px;
  padding: 0 12px;
  box-sizing: border-box;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1;
    min-width: 0;
  }
`;

const FilterSelect = styled.select`
  height: 36px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
`;

const FilterButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;

const AutoRefreshLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #899093;
  cursor: pointer;
  margin-left: auto;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 0;
  }
`;

const Table = styled.div`
  width: 100%;
`;

const MessageCard = styled.div<{ expanded?: boolean; hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  background: ${({ expanded }) => (expanded ? "#f0f4ff" : "transparent")};
  border-left: 3px solid ${({ expanded, hasError }) =>
    hasError ? "#e53e3e" : expanded ? "#4a90d9" : "transparent"};
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: ${({ expanded }) => (expanded ? "#f0f4ff" : "#fafbfc")};
  }
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
  }
`;

const MessageStatusIndicator = styled.div<{ status: "ok" | "error" | "pending" }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ status }) =>
    status === "ok" ? "#48bb78" : status === "error" ? "#e53e3e" : "#cbd5e0"};
`;

const MessageMainInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MessageTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const MessageEsn = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: #4a90d9;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 180px;
    font-size: 12px;
  }
`;

const MessageId = styled.span`
  font-family: monospace;
  font-size: 11px;
  color: #a0aec0;
  white-space: nowrap;
`;

const MessageTypeBadge = styled.span<{ type?: string }>`
  display: inline-flex;
  align-items: center;
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
  background: ${({ type }) => {
    switch (type) {
      case "ALERT": return "#e6f6ff";
      case "CONNECTION_STATE": return "#fefce8";
      case "REQUEST": return "#f0fff4";
      case "REQUEST_FEEDBACK": return "#faf5ff";
      default: return "#edf2f7";
    }
  }};
  color: ${({ type }) => {
    switch (type) {
      case "ALERT": return "#2b6cb0";
      case "CONNECTION_STATE": return "#975a16";
      case "REQUEST": return "#276749";
      case "REQUEST_FEEDBACK": return "#553c9a";
      default: return "#718096";
    }
  }};
`;

const MessageBottomLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MessageMeta = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #a0aec0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const PointsBadge = styled.span<{ count: number }>`
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 8px;
  background: ${({ count }) => (count > 0 ? "#f0fff4" : "#f7fafc")};
  color: ${({ count }) => (count > 0 ? "#276749" : "#a0aec0")};
`;

const ExpandArrow = styled.span<{ open: boolean }>`
  display: inline-block;
  font-size: 10px;
  transition: transform 0.15s ease;
  transform: ${({ open }) => (open ? "rotate(90deg)" : "rotate(0deg)")};
  color: #cbd5e0;
  flex-shrink: 0;
`;

const DetailPanel = styled.div`
  padding: 20px;
  background: #fafbfc;
  border-bottom: 2px solid #e2e8f0;
  border-left: 3px solid #4a90d9;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: 32px;
`;

const PageButton = styled(Button)`
  width: auto;
  min-width: 80px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

// ── Detail Panel Sub-Components ────────────────────────────

const SectionContainer = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ color }) => color || "#e8ecf1"};
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  &:hover {
    filter: brightness(0.97);
  }
`;

const SectionTitle = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #2d3748;
`;

const SectionBadge = styled.span<{ bg?: string; color?: string }>`
  font-family: monospace;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${({ bg }) => bg || "#cbd5e0"};
  color: ${({ color }) => color || "#2d3748"};
  font-weight: 600;
`;

const Arrow = styled.span<{ open: boolean }>`
  display: inline-block;
  font-size: 10px;
  transition: transform 0.15s ease;
  transform: ${({ open }) => (open ? "rotate(90deg)" : "rotate(0deg)")};
  color: #718096;
`;

const SectionBody = styled.div`
  padding: 12px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FieldLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 11px;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FieldValue = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: #2d3748;
`;

const SensorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: monospace;
  font-size: 12px;

  th {
    background: #edf2f7;
    padding: 6px 8px;
    text-align: right;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #718096;
    font-weight: 600;
    position: sticky;
    top: 0;
    white-space: nowrap;
  }

  th:first-of-type {
    text-align: left;
  }

  td {
    padding: 5px 8px;
    text-align: right;
    color: #2d3748;
    border-bottom: 1px solid #f0f0f0;
  }

  td:first-of-type {
    text-align: left;
    color: #718096;
  }

  tr:hover td {
    background: #f7fafc;
  }
`;

const SensorTableWrap = styled.div`
  max-height: 400px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
`;

const ErrorBanner = styled.div`
  padding: 10px 14px;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 6px;
  color: #c53030;
  font-family: monospace;
  font-size: 12px;
  margin-bottom: 12px;
`;

const TopicBar = styled.div`
  padding: 8px 12px;
  background: #edf2f7;
  border-radius: 6px;
  font-family: monospace;
  font-size: 12px;
  color: #4a5568;
  margin-bottom: 12px;
  word-break: break-all;
`;

const RawJsonPre = styled.pre`
  margin: 0;
  padding: 12px;
  background: #1a202c;
  color: #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  overflow: auto;
  max-height: 300px;
  white-space: pre-wrap;
  word-break: break-all;
`;

// ── Constants ──────────────────────────────────────────────

const MESSAGE_TYPES: Record<number, string> = {
  0: "INVALID",
  1: "REQUEST",
  2: "REQUEST_FEEDBACK",
  3: "CONNECTION_STATE",
  4: "ALERT",
};

// Mirrors `enum AlertType` in backend-ai/src/proto/ggp.proto — copy it from
// there, the order is not the obvious one. It once drifted and mislabelled 13
// of the 15 alerts.
const ALERT_TYPES: Record<number, string> = {
  0: "INVALID",
  1: "TCU_RESET_REQUEST",
  2: "CONFIG_CHANGE_REQUEST",
  3: "FOTA_REQUEST",
  4: "LOW_BATTERY",
  5: "LOW_BATTERY_CANCEL",
  6: "IGNITION_ON",
  7: "IGNITION_OFF",
  8: "ENGINE_RUN",
  9: "ENGINE_STOP",
  10: "CURRENT_STATUS",
  11: "CONFIG_GET_REQUEST",
  12: "TCU_META_GET_REQUEST",
  13: "AUTH_STATE_CHANGE_REQUEST",
  14: "CURRENT_STATUS_UPDATE_REQUEST",
  15: "SLICE_STATUS",
};

const TCU_AUTH_STATES: Record<number, string> = {
  0: "INVALID",
  1: "NOT_AUTHORIZED",
  2: "AUTHORIZED",
};

const TCU_CONFIG_STATES: Record<number, string> = {
  0: "INVALID",
  1: "NOT_CONFIGURED",
  2: "CONFIGURED",
};

const POWERPACK_STATUSES: Record<number, string> = {
  0: "INVALID",
  1: "IGNITION_OFF",
  2: "IGN_ON_ENG_OFF",
  3: "IGN_ON_ENG_ON",
};

const GSM_REG_STATUSES: Record<number, string> = {
  0: "Not registered",
  1: "Home network",
  2: "Searching",
  3: "Denied",
  4: "Reserved",
  5: "Roaming",
};

const REAPER_STATUSES: Record<number, string> = {
  0: "INVALID",
  1: "UP",
  2: "DOWN",
};

const MIL_STATUSES: Record<number, string> = {
  0: "INVALID",
  1: "OFF",
  2: "ON",
};

// RootCameraInfoStatus (implement_data.root_camera_info) — device camera health.
const ROOT_CAMERA_STATUSES: Record<number, string> = {
  0: "INVALID",
  1: "OFF",
  2: "ON",
  3: "ERROR",
};

// RootInfoStatus (implement_data.root_info) — root/mass-sensor controller health.
const ROOT_SENSOR_STATUSES: Record<number, string> = {
  0: "INVALID",
  1: "OFF",
  2: "ON",
  3: "ERROR",
};

const PAGE_SIZE = 50;

// ── Helper Functions ───────────────────────────────────────

const formatUnixTime = (ts: number) => {
  if (!ts) return "-";
  return new Date(ts * 1000).toLocaleString();
};

const formatCoord = (v: number) => (v ? v.toFixed(6) : "-");

const formatBatteryVoltage = (mv: number | undefined) => {
  if (mv == null) return "-";
  return `${(mv / 1000).toFixed(3)} V`;
};

// ── Collapsible Section Component ──────────────────────────

const CollapsibleSection: React.FC<{
  title: string;
  color?: string;
  badge?: string;
  badgeBg?: string;
  badgeColor?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, color, badge, badgeBg, badgeColor, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <SectionContainer>
      <SectionHeader color={color} onClick={() => setOpen(!open)}>
        <Arrow open={open}>&#9654;</Arrow>
        <SectionTitle>{title}</SectionTitle>
        {badge && <SectionBadge bg={badgeBg} color={badgeColor}>{badge}</SectionBadge>}
      </SectionHeader>
      {open && <SectionBody>{children}</SectionBody>}
    </SectionContainer>
  );
};

// ── Message Detail Renderer ────────────────────────────────

const MessageDetailView: React.FC<{ detail: AdminMessageDetail }> = ({ detail }) => {
  const parsed = detail.parsed;

  if (detail.parseError) {
    return (
      <>
        <TopicBar>Topic: {detail.topic || "N/A"}</TopicBar>
        <ErrorBanner>Parse Error: {detail.parseError}</ErrorBanner>
        {parsed && (
          <CollapsibleSection title="Raw Parsed Data" color="#edf2f7">
            <RawJsonPre>{JSON.stringify(parsed, null, 2)}</RawJsonPre>
          </CollapsibleSection>
        )}
      </>
    );
  }

  if (!parsed) {
    return (
      <>
        <TopicBar>Topic: {detail.topic || "N/A"}</TopicBar>
        <Text size={13} color="#a0aec0">No parsed data available</Text>
      </>
    );
  }

  const alert = parsed.alert;
  const connState = parsed.connectionState;
  const messageTypeName = MESSAGE_TYPES[parsed.type] || String(parsed.type);

  return (
    <>
      <TopicBar>
        Topic: {detail.topic || "N/A"}
        {" | "}
        Type: <strong>{messageTypeName}</strong>
        {" | "}
        Received: {new Date(detail.receivedAt).toLocaleString()}
      </TopicBar>

      {/* Connection State Messages */}
      {connState && (
        <CollapsibleSection
          title="Connection State"
          color="#e6f6ff"
          badge={`State: ${connState.connectionState}`}
          badgeBg="#bee3f8"
          badgeColor="#2b6cb0"
          defaultOpen
        >
          <FieldGrid>
            <Field>
              <FieldLabel>ESN</FieldLabel>
              <FieldValue>{connState.esn}</FieldValue>
            </Field>
            <Field>
              <FieldLabel>Connection State</FieldLabel>
              <FieldValue>{connState.connectionState}</FieldValue>
            </Field>
          </FieldGrid>
          {connState.tcuMeta && (
            <div style={{ marginTop: 12 }}>
              <FieldLabel>Firmware</FieldLabel>
              <FieldGrid style={{ marginTop: 4 }}>
                {Object.entries(connState.tcuMeta).map(([k, v]) => (
                  <Field key={k}>
                    <FieldLabel>{k}</FieldLabel>
                    <FieldValue>{String(v)}</FieldValue>
                  </Field>
                ))}
              </FieldGrid>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Alert Messages */}
      {alert && (
        <>
          {/* TCU Base Section */}
          <CollapsibleSection
            title="TCU Base"
            color="#e6f6ff"
            badge={alert.alertType != null ? (ALERT_TYPES[alert.alertType] || `Type ${alert.alertType}`) : undefined}
            badgeBg="#bee3f8"
            badgeColor="#2b6cb0"
            defaultOpen
          >
            <FieldGrid>
              <Field>
                <FieldLabel>ESN</FieldLabel>
                <FieldValue>{alert.esn || alert.tcuBase?.esn || "-"}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>IMEI</FieldLabel>
                <FieldValue>{alert.imei || alert.tcuBase?.imei || "-"}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>ICCID</FieldLabel>
                <FieldValue>{alert.iccid || alert.tcuBase?.iccid || "-"}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Date/Time</FieldLabel>
                <FieldValue>{formatUnixTime(alert.dateTime || alert.tcuBase?.dateTime)}</FieldValue>
              </Field>
              {alert.tcuBase?.messageId != null && (
                <Field>
                  <FieldLabel>Message ID</FieldLabel>
                  <FieldValue>{alert.tcuBase.messageId}</FieldValue>
                </Field>
              )}
              {alert.tcuBase?.tcuAuthState != null && (
                <Field>
                  <FieldLabel>Auth State</FieldLabel>
                  <FieldValue>
                    <SectionBadge
                      bg={alert.tcuBase.tcuAuthState === 2 ? "#c6f6d5" : "#fed7d7"}
                      color={alert.tcuBase.tcuAuthState === 2 ? "#276749" : "#c53030"}
                    >
                      {TCU_AUTH_STATES[alert.tcuBase.tcuAuthState] || alert.tcuBase.tcuAuthState}
                    </SectionBadge>
                  </FieldValue>
                </Field>
              )}
              {alert.tcuBase?.tcuConfigState != null && (
                <Field>
                  <FieldLabel>Config State</FieldLabel>
                  <FieldValue>
                    <SectionBadge
                      bg={alert.tcuBase.tcuConfigState === 2 ? "#c6f6d5" : "#fefcbf"}
                      color={alert.tcuBase.tcuConfigState === 2 ? "#276749" : "#975a16"}
                    >
                      {TCU_CONFIG_STATES[alert.tcuBase.tcuConfigState] || alert.tcuBase.tcuConfigState}
                    </SectionBadge>
                  </FieldValue>
                </Field>
              )}
            </FieldGrid>

            {/* TCU Meta / Firmware */}
            {alert.tcuMeta && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #e2e8f0" }}>
                <FieldLabel style={{ marginBottom: 6, display: "block" }}>Firmware Versions</FieldLabel>
                <FieldGrid>
                  {Object.entries(alert.tcuMeta).map(([k, v]) => (
                    <Field key={k}>
                      <FieldLabel>{k.replace(/([A-Z])/g, " $1").trim()}</FieldLabel>
                      <FieldValue>{String(v)}</FieldValue>
                    </Field>
                  ))}
                </FieldGrid>
              </div>
            )}
          </CollapsibleSection>

          {/* Current Status Section */}
          {alert.currentStatus && (
            <CollapsibleSection
              title="Current Status"
              color="#fefce8"
              badge={alert.currentStatus.powerpackStatus != null
                ? POWERPACK_STATUSES[alert.currentStatus.powerpackStatus] || `Status ${alert.currentStatus.powerpackStatus}`
                : undefined}
              badgeBg={alert.currentStatus.powerpackStatus === 3 ? "#c6f6d5" : alert.currentStatus.powerpackStatus === 2 ? "#fefcbf" : "#fed7d7"}
              badgeColor={alert.currentStatus.powerpackStatus === 3 ? "#276749" : alert.currentStatus.powerpackStatus === 2 ? "#975a16" : "#c53030"}
              defaultOpen
            >
              <FieldGrid>
                {alert.currentStatus.gpsInfo && (
                  <>
                    <Field>
                      <FieldLabel>GPS Time</FieldLabel>
                      <FieldValue>{formatUnixTime(alert.currentStatus.gpsInfo.dateTime)}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Position</FieldLabel>
                      <FieldValue>
                        {formatCoord(alert.currentStatus.gpsInfo.latitude)}, {formatCoord(alert.currentStatus.gpsInfo.longitude)}
                      </FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Altitude</FieldLabel>
                      <FieldValue>{alert.currentStatus.gpsInfo.altitude?.toFixed(1) || "-"} m</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Speed</FieldLabel>
                      <FieldValue>{alert.currentStatus.gpsInfo.speed ?? "-"}</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Course</FieldLabel>
                      <FieldValue>{alert.currentStatus.gpsInfo.course ?? "-"}°</FieldValue>
                    </Field>
                    <Field>
                      <FieldLabel>Satellites</FieldLabel>
                      <FieldValue>{alert.currentStatus.gpsInfo.satQty ?? "-"}</FieldValue>
                    </Field>
                  </>
                )}
                {alert.currentStatus.batteryVoltage != null && (
                  <Field>
                    <FieldLabel>Battery</FieldLabel>
                    <FieldValue>{formatBatteryVoltage(alert.currentStatus.batteryVoltage)}</FieldValue>
                  </Field>
                )}
                {alert.currentStatus.signalStrength != null && (
                  <Field>
                    <FieldLabel>Signal Strength</FieldLabel>
                    <FieldValue>{alert.currentStatus.signalStrength} dBm</FieldValue>
                  </Field>
                )}
                {alert.currentStatus.gsmRegistrationStatus != null && (
                  <Field>
                    <FieldLabel>GSM Registration</FieldLabel>
                    <FieldValue>
                      <SectionBadge
                        bg={[1, 5].includes(alert.currentStatus.gsmRegistrationStatus) ? "#c6f6d5" : "#fefcbf"}
                        color={[1, 5].includes(alert.currentStatus.gsmRegistrationStatus) ? "#276749" : "#975a16"}
                      >
                        {GSM_REG_STATUSES[alert.currentStatus.gsmRegistrationStatus] || alert.currentStatus.gsmRegistrationStatus}
                      </SectionBadge>
                    </FieldValue>
                  </Field>
                )}
                {alert.currentStatus.gsmCellBasesQty != null && (
                  <Field>
                    <FieldLabel>Cell Bases</FieldLabel>
                    <FieldValue>{alert.currentStatus.gsmCellBasesQty}</FieldValue>
                  </Field>
                )}
                {alert.currentStatus.gsmRoaming != null && (
                  <Field>
                    <FieldLabel>Roaming</FieldLabel>
                    <FieldValue>{alert.currentStatus.gsmRoaming === 0 ? "Home" : alert.currentStatus.gsmRoaming === 1 ? "International" : "Intranetwork"}</FieldValue>
                  </Field>
                )}
              </FieldGrid>
            </CollapsibleSection>
          )}

          {/* Camera health (implement_data.root_camera_info) */}
          {alert.currentStatus?.implementData?.rootCameraInfo && (() => {
            const rc = alert.currentStatus.implementData.rootCameraInfo;
            const status = rc.status;
            return (
              <CollapsibleSection
                title="Camera"
                color="#eef6ff"
                badge={status != null ? (ROOT_CAMERA_STATUSES[status] || `Status ${status}`) : undefined}
                badgeBg={status === 2 ? "#c6f6d5" : status === 3 ? "#fed7d7" : "#e2e8f0"}
                badgeColor={status === 2 ? "#276749" : status === 3 ? "#c53030" : "#718096"}
                defaultOpen
              >
                <FieldGrid>
                  {rc.fps != null && (
                    <Field><FieldLabel>FPS</FieldLabel><FieldValue>{rc.fps}</FieldValue></Field>
                  )}
                  {rc.tempC != null && (
                    <Field><FieldLabel>Temp</FieldLabel><FieldValue>{rc.tempC}°C</FieldValue></Field>
                  )}
                  {rc.modelVersion != null && (
                    <Field><FieldLabel>Model ver</FieldLabel><FieldValue>{rc.modelVersion}</FieldValue></Field>
                  )}
                  {rc.protocolVersion != null && (
                    <Field><FieldLabel>Protocol ver</FieldLabel><FieldValue>{rc.protocolVersion}</FieldValue></Field>
                  )}
                  {rc.fwVersion ? (
                    <Field><FieldLabel>Firmware</FieldLabel><FieldValue>{rc.fwVersion}</FieldValue></Field>
                  ) : null}
                  {rc.uptimeS != null && (
                    <Field><FieldLabel>Uptime</FieldLabel><FieldValue>{rc.uptimeS.toLocaleString()} s</FieldValue></Field>
                  )}
                  {rc.errorId ? (
                    <Field>
                      <FieldLabel>Error</FieldLabel>
                      <FieldValue>#{rc.errorId} {rc.errorDescription || ""}</FieldValue>
                    </Field>
                  ) : null}
                </FieldGrid>
              </CollapsibleSection>
            );
          })()}

          {/* Root sensor health (implement_data.root_info) — mass sensor + calibration */}
          {alert.currentStatus?.implementData?.rootInfo && (() => {
            const ri = alert.currentStatus.implementData.rootInfo;
            const status = ri.status;
            const offset = Array.isArray(ri.calibrationOffset) ? ri.calibrationOffset : null;
            return (
              <CollapsibleSection
                title="Root Sensor"
                color="#f5f0ff"
                badge={status != null ? (ROOT_SENSOR_STATUSES[status] || `Status ${status}`) : undefined}
                badgeBg={status === 2 ? "#c6f6d5" : status === 3 ? "#fed7d7" : "#e2e8f0"}
                badgeColor={status === 2 ? "#276749" : status === 3 ? "#c53030" : "#718096"}
                defaultOpen
              >
                <FieldGrid>
                  <Field>
                    <FieldLabel>Calibration offset</FieldLabel>
                    <FieldValue>{offset ? (offset.length ? offset.join(", ") : "—") : "—"}</FieldValue>
                  </Field>
                  {ri.fwVersion ? (
                    <Field><FieldLabel>Firmware</FieldLabel><FieldValue>{ri.fwVersion}</FieldValue></Field>
                  ) : null}
                  {ri.uptimeS != null && (
                    <Field><FieldLabel>Uptime</FieldLabel><FieldValue>{ri.uptimeS.toLocaleString()} s</FieldValue></Field>
                  )}
                  {ri.errorId ? (
                    <Field>
                      <FieldLabel>Error</FieldLabel>
                      <FieldValue>#{ri.errorId} {ri.errorDescription || ""}</FieldValue>
                    </Field>
                  ) : null}
                </FieldGrid>
              </CollapsibleSection>
            );
          })()}

          {/* CAN Data Section */}
          {alert.currentStatus?.canData && (() => {
            const can = alert.currentStatus.canData;
            const hasEngineData = can.engineSpeed != null || can.engineCoolantTemperature != null ||
              can.engineFuelTemperature != null || can.engineOilTemperature != null;
            const hasVehicleData = can.speed != null || can.fuelLevelPercent != null ||
              can.odometerKm != null || can.totalOperationHours != null || can.hopperLevelPercent != null;
            if (!hasEngineData && !hasVehicleData && can.milStatus == null) return null;
            return (
              <CollapsibleSection
                title="CAN Bus Data"
                color="#e8e0f0"
                badge={can.milStatus != null
                  ? `MIL: ${MIL_STATUSES[can.milStatus] || can.milStatus}`
                  : undefined}
                badgeBg={can.milStatus === 2 ? "#fed7d7" : can.milStatus === 1 ? "#c6f6d5" : "#e2e8f0"}
                badgeColor={can.milStatus === 2 ? "#c53030" : can.milStatus === 1 ? "#276749" : "#718096"}
                defaultOpen
              >
                <FieldGrid>
                  {can.engineSpeed != null && (
                    <Field>
                      <FieldLabel>Engine Speed</FieldLabel>
                      <FieldValue>{can.engineSpeed} RPM</FieldValue>
                    </Field>
                  )}
                  {can.speed != null && (
                    <Field>
                      <FieldLabel>Vehicle Speed (CAN)</FieldLabel>
                      <FieldValue>{can.speed} km/h</FieldValue>
                    </Field>
                  )}
                  {can.engineCoolantTemperature != null && (
                    <Field>
                      <FieldLabel>Coolant Temp</FieldLabel>
                      <FieldValue>{can.engineCoolantTemperature.toFixed(1)} °C</FieldValue>
                    </Field>
                  )}
                  {can.engineFuelTemperature != null && (
                    <Field>
                      <FieldLabel>Fuel Temp</FieldLabel>
                      <FieldValue>{can.engineFuelTemperature.toFixed(1)} °C</FieldValue>
                    </Field>
                  )}
                  {can.engineOilTemperature != null && (
                    <Field>
                      <FieldLabel>Oil Temp</FieldLabel>
                      <FieldValue>{can.engineOilTemperature.toFixed(1)} °C</FieldValue>
                    </Field>
                  )}
                  {can.fuelLevelPercent != null && (
                    <Field>
                      <FieldLabel>Fuel Level</FieldLabel>
                      <FieldValue>{can.fuelLevelPercent}%</FieldValue>
                    </Field>
                  )}
                  {can.odometerKm != null && (
                    <Field>
                      <FieldLabel>Odometer</FieldLabel>
                      <FieldValue>{can.odometerKm.toLocaleString()} km</FieldValue>
                    </Field>
                  )}
                  {can.totalOperationHours != null && (
                    <Field>
                      <FieldLabel>Operation Hours</FieldLabel>
                      <FieldValue>{can.totalOperationHours.toLocaleString()} h</FieldValue>
                    </Field>
                  )}
                  {can.hopperLevelPercent != null && (
                    <Field>
                      <FieldLabel>Hopper Level</FieldLabel>
                      <FieldValue>{can.hopperLevelPercent}%</FieldValue>
                    </Field>
                  )}
                  {can.milStatus != null && (
                    <Field>
                      <FieldLabel>MIL (Check Engine)</FieldLabel>
                      <FieldValue>
                        <SectionBadge
                          bg={can.milStatus === 2 ? "#fed7d7" : can.milStatus === 1 ? "#c6f6d5" : "#e2e8f0"}
                          color={can.milStatus === 2 ? "#c53030" : can.milStatus === 1 ? "#276749" : "#718096"}
                        >
                          {MIL_STATUSES[can.milStatus] || can.milStatus}
                        </SectionBadge>
                      </FieldValue>
                    </Field>
                  )}
                </FieldGrid>
              </CollapsibleSection>
            );
          })()}

          {/* Current Position (fallback if no currentStatus) */}
          {!alert.currentStatus && alert.currentPosition && (
            <CollapsibleSection title="Current Position" color="#e6fffa" defaultOpen>
              <FieldGrid>
                <Field>
                  <FieldLabel>Latitude</FieldLabel>
                  <FieldValue>{formatCoord(alert.currentPosition.latitude)}</FieldValue>
                </Field>
                <Field>
                  <FieldLabel>Longitude</FieldLabel>
                  <FieldValue>{formatCoord(alert.currentPosition.longitude)}</FieldValue>
                </Field>
              </FieldGrid>
            </CollapsibleSection>
          )}

          {/* Sensor Values Section */}
          {alert.sensorValues && alert.sensorValues.length > 0 && (() => {
            const hasCam = alert.sensorValues.some(
              (sv: any) => sv.cvPotatoCount != null || sv.cvStoneCount != null
            );
            return (
            <CollapsibleSection
              title="Sensor Values"
              color="#f0fff4"
              badge={`${alert.sensorValues.length} points`}
              badgeBg="#c6f6d5"
              badgeColor="#276749"
              defaultOpen={false}
            >
              <SensorTableWrap>
                <SensorTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Time</th>
                      <th>Lat</th>
                      <th>Lon</th>
                      <th>Alt</th>
                      <th>Speed</th>
                      <th>Course</th>
                      <th>Sats</th>
                      <th>Elevator</th>
                      <th>Freq</th>
                      <th>Reaper</th>
                      <th>Lift%</th>
                      <th>Moist%</th>
                      {hasCam && (
                        <>
                          <th>Potato</th>
                          <th>Stones</th>
                          <th>Sizes</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {alert.sensorValues.map((sv: any, i: number) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatUnixTime(sv.dateTime)}</td>
                        <td>{formatCoord(sv.latitude)}</td>
                        <td>{formatCoord(sv.longitude)}</td>
                        <td>{sv.altitude?.toFixed(1)}</td>
                        <td>{sv.speed}</td>
                        <td>{sv.course}°</td>
                        <td>{sv.satQty}</td>
                        <td style={{ fontWeight: sv.elevatorSensorValue > 200 ? 700 : 400, color: sv.elevatorSensorValue > 200 ? "#276749" : "#2d3748" }}>
                          {sv.elevatorSensorValue}
                        </td>
                        <td style={{ color: sv.elevatorSensorReadFreq > 0 ? "#276749" : "#a0aec0" }}>
                          {sv.elevatorSensorReadFreq}
                        </td>
                        <td>
                          <SectionBadge
                            bg={sv.reaperStatus === 2 ? "#c6f6d5" : sv.reaperStatus === 1 ? "#fefcbf" : "#e2e8f0"}
                            color={sv.reaperStatus === 2 ? "#276749" : sv.reaperStatus === 1 ? "#975a16" : "#718096"}
                          >
                            {REAPER_STATUSES[sv.reaperStatus] || sv.reaperStatus}
                          </SectionBadge>
                        </td>
                        <td>{sv.reaperLiftPercent}%</td>
                        <td>{sv.moisturePercent}%</td>
                        {hasCam && (
                          <>
                            <td style={{ fontWeight: sv.cvPotatoCount > 0 ? 700 : 400, color: "#276749" }}>
                              {sv.cvPotatoCount ?? "—"}
                            </td>
                            <td style={{ color: sv.cvStoneCount > 0 ? "#9b2c2c" : "#a0aec0" }}>
                              {sv.cvStoneCount ?? "—"}
                            </td>
                            <td style={{ whiteSpace: "nowrap", color: "#4a5568" }}>
                              {Array.isArray(sv.cvSizeHist) ? sv.cvSizeHist.join(", ") : "—"}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </SensorTable>
              </SensorTableWrap>
            </CollapsibleSection>
            );
          })()}

          {/* Raw JSON fallback */}
          <CollapsibleSection title="Raw JSON" color="#edf2f7">
            <RawJsonPre>{JSON.stringify(parsed, null, 2)}</RawJsonPre>
          </CollapsibleSection>
        </>
      )}

      {/* Non-alert, non-connection messages */}
      {!alert && !connState && (
        <CollapsibleSection title="Raw Data" color="#edf2f7" defaultOpen>
          <RawJsonPre>{JSON.stringify(parsed, null, 2)}</RawJsonPre>
        </CollapsibleSection>
      )}
    </>
  );
};

// ── Main Component ─────────────────────────────────────────

interface AdminMessagesTabProps {
  fixedDeviceEsn?: string;
}

const AdminMessagesTab: React.FC<AdminMessagesTabProps> = ({ fixedDeviceEsn }) => {
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [deviceEsn, setDeviceEsn] = useState(fixedDeviceEsn ?? "");
  const [messageType, setMessageType] = useState<string>("");
  // The ESN box queries once typing pauses, not on every keystroke.
  const qDeviceEsn = useDebouncedValue(deviceEsn);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMessages({
        deviceEsn: qDeviceEsn || undefined,
        messageType: messageType ? parseInt(messageType, 10) : undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setMessages(data.messages);
      setTotal(data.total);
    } catch (e) {
      console.error("Failed to fetch messages:", e);
    } finally {
      setLoading(false);
    }
  }, [qDeviceEsn, messageType, offset]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Poll every 30s while auto-refresh is on, but only when the tab is visible
  // (a backgrounded tab polling the DB keeps the Neon compute from suspending).
  usePolling(fetchMessages, 30000, autoRefresh);

  const handleFilter = () => {
    setOffset(0);
    fetchMessages();
  };

  const handleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetailLoading(true);
    try {
      const d = await getMessage(id);
      setDetail(d);
    } catch (e) {
      console.error("Failed to fetch message detail:", e);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString();
  };

  return (
    <div>
      <FilterRow>
        {!fixedDeviceEsn && (
          <FilterInput
            placeholder="Device ESN..."
            value={deviceEsn}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDeviceEsn(e.target.value)
            }
          />
        )}
        <FilterSelect
          value={messageType}
          onChange={(e) => setMessageType(e.target.value)}
        >
          <option value="">All types</option>
          {Object.entries(MESSAGE_TYPES).map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </FilterSelect>
        <FilterButton kind="outline" onClick={handleFilter}>
          Filter
        </FilterButton>
        <FilterButton kind="outline" onClick={() => fetchMessages()}>
          Refresh
        </FilterButton>
        <AutoRefreshLabel>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (10s)
        </AutoRefreshLabel>
      </FilterRow>

      <Table>
        {loading ? (
          <LoadingWrap>
            <Spinner />
          </LoadingWrap>
        ) : (
          messages.map((msg) => {
            const typeName = msg.messageType != null
              ? MESSAGE_TYPES[msg.messageType] || String(msg.messageType)
              : undefined;
            const status = msg.parsed ? "ok" as const : msg.parseError ? "error" as const : "pending" as const;
            const points = msg.sensorValuesCount ?? 0;
            const isExpanded = expandedId === msg.id;

            return (
              <React.Fragment key={msg.id}>
                <MessageCard
                  expanded={isExpanded}
                  hasError={status === "error"}
                  onClick={() => handleExpand(msg.id)}
                >
                  <MessageStatusIndicator status={status} />
                  <MessageMainInfo>
                    <MessageTopLine>
                      <MessageEsn>{msg.deviceEsn || "Unknown device"}</MessageEsn>
                      {typeName && <MessageTypeBadge type={typeName}>{typeName}</MessageTypeBadge>}
                      {points > 0 && <PointsBadge count={points}>{points} pts</PointsBadge>}
                    </MessageTopLine>
                    <MessageBottomLine>
                      <MessageId>#{msg.id}</MessageId>
                      <MessageMeta>{formatTime(msg.receivedAt)}</MessageMeta>
                    </MessageBottomLine>
                  </MessageMainInfo>
                  <ExpandArrow open={isExpanded}>&#9654;</ExpandArrow>
                </MessageCard>
                {isExpanded && (
                  <DetailPanel>
                    {detailLoading ? (
                      <LoadingWrap><Spinner /></LoadingWrap>
                    ) : detail ? (
                      <MessageDetailView detail={detail} />
                    ) : (
                      <Text size={13} color="#c53030">Failed to load detail</Text>
                    )}
                  </DetailPanel>
                )}
              </React.Fragment>
            );
          })
        )}
      </Table>

      {!loading && total > PAGE_SIZE && (
        <Pagination>
          <PageButton
            kind="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </PageButton>
          <Text size={13} color="#899093">
            {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Text>
          <PageButton
            kind="outline"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </PageButton>
        </Pagination>
      )}
    </div>
  );
};

export default AdminMessagesTab;
